/**
 * CostByAgentChart - Vertical bar chart showing top agents by selected metric
 *
 * Features:
 * - Aggregates cost/token data by agent across all timestamps
 * - Shows top 10 agents sorted by selected metric
 * - Vertical bars (agents on X-axis, values on Y-axis)
 * - Legend at bottom
 * - Uses model color palette adapted for agents
 */

import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { palette, typography } from '../../utils/designTokens';
import { getChartColors } from '../../utils/chartColors';
import { serializeFilters } from '../../utils/urlBuilder';

/**
 * Process cost data to aggregate by agent
 */
const processDataByAgent = (agentCostOverTimeData, selectedMetric) => {
  if (!agentCostOverTimeData || agentCostOverTimeData.length === 0) {
    return { chartData: [], agentList: [], agentColors: {} };
  }

  // Aggregate totals by agent
  const agentTotals = {};

  agentCostOverTimeData.forEach(({ agents }) => {
    agents.forEach((agent) => {
      const agentKey = agent.agent_name;

      if (!agentTotals[agentKey]) {
        agentTotals[agentKey] = {
          agent_name: agent.agent_name,
          cost_usd: 0,
          total_tokens: 0,
          prompt_tokens: 0,
          completion_tokens: 0,
          cached_tokens: 0
        };
      }

      agentTotals[agentKey].cost_usd += agent.cost_usd || 0;
      agentTotals[agentKey].total_tokens += agent.total_tokens || 0;
      agentTotals[agentKey].prompt_tokens += agent.prompt_tokens || 0;
      agentTotals[agentKey].completion_tokens += agent.completion_tokens || 0;
      agentTotals[agentKey].cached_tokens += agent.cached_tokens || 0;
    });
  });

  // Convert to array and sort by selected metric
  let agentArray = Object.values(agentTotals);

  // Filter out agents with 0 value for the selected metric
  agentArray = agentArray.filter(agent => {
    const value = agent[selectedMetric] || 0;
    return value > 0;
  });

  // Sort by selected metric (descending)
  agentArray.sort((a, b) => {
    const aValue = a[selectedMetric] || 0;
    const bValue = b[selectedMetric] || 0;
    return bValue - aValue;
  });

  // Take top 10 (after filtering zeros)
  agentArray = agentArray.slice(0, 10);

  // Get agent names list
  const agentList = agentArray.map(d => d.agent_name);

  // Generate agent colors (reuse chart color logic)
  const agentColors = getChartColors(agentList);

  // Transform data for vertical bar chart
  // Create one data point with all agents as separate keys
  const chartData = [{
    category: 'Agents'  // Single category for all agents
  }];

  agentArray.forEach(agent => {
    chartData[0][agent.agent_name] = agent[selectedMetric] || 0;
    // Store metadata for tooltip
    chartData[0][`${agent.agent_name}_data`] = agent;
  });

  return { chartData, agentList, agentColors };
};

const CostByAgentChart = ({ data, selectedMetric, timeWindow, appId, environment }) => {
  const navigate = useNavigate();
  const [highlightedAgent, setHighlightedAgent] = React.useState(null);

  // Process data
  const { chartData, agentList, agentColors } = useMemo(
    () => processDataByAgent(data, selectedMetric),
    [data, selectedMetric]
  );

  // Handle bar click for drilldown (navigate to Traces page filtered by this agent)
  const handleBarClick = (agentName) => {
    if (!agentName) return;

    // Map selectedMetric to sort parameter
    const sortMapping = {
      'cost_usd': 'cost_desc',
      'total_tokens': 'total_tokens_desc',
      'prompt_tokens': 'prompt_tokens_desc',
      'completion_tokens': 'completion_tokens_desc',
      'cached_tokens': 'cached_tokens_desc'
    };
    const sortParam = sortMapping[selectedMetric] || 'cost_desc';

    // Navigate to Traces page filtered by this agent
    const filters = {
      agent: agentName,
      sort: sortParam,
      env: environment,
      range: timeWindow,
      from: 'cost'  // Track that user came from Cost tab for breadcrumb
    };
    const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
    navigate(url);
  };

  const hasData = agentList.length > 0;

  const containerRef = React.useRef(null);

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '14px'
        }}
      >
        No data available for the selected time window
      </div>
    );
  }

  // Format Y-axis labels (values) - matching CostByModelChart exactly
  const formatYAxis = (value) => {
    if (selectedMetric === 'cost_usd') {
      // Format as dollars
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      if (value >= 1) return `$${value.toFixed(0)}`;
      if (value >= 0.01) return `$${value.toFixed(2)}`;
      if (value > 0) return `$${value.toFixed(4)}`;
      return '$0';
    } else {
      // Format as tokens (K or M)
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
      return value.toFixed(0);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={280} ref={containerRef}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
          barCategoryGap="10%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={palette.support.gridline} opacity={0.5} />
          <XAxis
            dataKey="category"
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
            hide={true}  // Hide X-axis since we show agents in legend
          />
          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: palette.neutrals.panelQuaternary,
              border: `1px solid ${palette.neutrals.borderStrong}`,
              borderRadius: '4px',
              fontSize: '12px',
              padding: '8px'
            }}
            labelStyle={{ color: palette.text.heading, fontWeight: 600 }}
            itemStyle={{ color: palette.text.primary }}
            formatter={(value, name) => {
              // Format the value with proper units
              let displayValue;
              if (selectedMetric === 'cost_usd') {
                displayValue = `$${value.toFixed(4)}`;
              } else {
                displayValue = value.toLocaleString();
              }

              return [displayValue, name];
            }}
            cursor={false}
          />
          <Legend
            wrapperStyle={{ fontSize: '10px', paddingTop: '5px' }}
            iconType="square"
          />

          {/* One Bar per agent */}
          {agentList.map((agentName) => (
            <Bar
              key={agentName}
              dataKey={agentName}
              fill={agentColors[agentName]}
              fillOpacity={highlightedAgent === agentName ? 0.9 : 0.7}
              onClick={() => handleBarClick(agentName)}
              onMouseEnter={() => setHighlightedAgent(agentName)}
              onMouseLeave={() => setHighlightedAgent(null)}
              style={{ cursor: 'pointer' }}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
  );
};

export default CostByAgentChart;
